import { create } from 'zustand';
import { subDays, format } from 'date-fns';
import { servicesContainer } from '@/core/di/services-container';
import { useOperatorStore } from '@/core/stores/operator-store';
import { analyzeDelta, type SmartSuggestion } from '../utils/smart-suggestions';
import type {
  IrregularDeltaItem,
  DeltaMeterValue,
  FacetOption,
  DataErrorFilters,
  HandleType,
  HandleActionRequest,
  PeriodType,
} from '../types/data-error-types';

const DEFAULT_PERIOD_TYPE: PeriodType = 'last60Days';
const DEFAULT_PAGE_SIZE = 50;

export interface AnalyzeGroupEntry {
  item: IrregularDeltaItem;
  meterValues: DeltaMeterValue[];
  suggestion: SmartSuggestion | null;
  isLoading: boolean;
  error: string | null;
}
const DEFAULT_SURROUND_SIZE = 3;

// Status key used to pre-select Pending filter (HIVE-5554 behaviour)
const DEFAULT_STATUS_KEY = 'false'; // 'handled eq false' = Pending
const DEFAULT_LOCATION_USAGE_KEY = 'SharedBuilding';

function toIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function getDefaultFilters(): DataErrorFilters {
  const today = new Date();
  return {
    search: '',
    periodFrom: toIsoDate(subDays(today, 60)),
    periodTo: toIsoDate(today),
    periodType: DEFAULT_PERIOD_TYPE,
    selectedStatuses: [],
    selectedLocationUsages: [],
    actorId: null,
  };
}

interface DataErrorStore {
  // List state
  items: IrregularDeltaItem[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  filters: DataErrorFilters;

  // Filter options (from /filters endpoint)
  statusOptions: FacetOption[];
  locationUsageOptions: FacetOption[];
  isLoadingFilters: boolean;

  // Blade state
  selectedItem: IrregularDeltaItem | null;
  meterValues: DeltaMeterValue[];
  isLoadingMeterValues: boolean;
  meterValuesError: string | null;
  surroundSize: number;

  // Action state
  handlingId: number | null;
  checkedIds: Set<number>;
  isExporting: boolean;

  // Group analysis state
  analyzeGroupEntries: AnalyzeGroupEntry[];
  analyzeGroupChecked: Set<number>;
  isApplyingGroupFix: boolean;
  groupAnalysisError: string | null;

  // Actions
  loadItems: () => Promise<void>;
  loadFilters: () => Promise<void>;
  loadPage: (page: number) => Promise<void>;
  setPageSize: (size: number) => void;
  setFilters: (f: Partial<DataErrorFilters>) => void;
  selectItem: (item: IrregularDeltaItem | null) => void;
  previewItem: (item: IrregularDeltaItem, meterValues: DeltaMeterValue[]) => void;
  setSurroundSize: (n: number) => void;
  handleItem: (irregularId: number, type: HandleType, comment: string) => Promise<void>;
  batchHandle: (type: HandleType, comment: string) => Promise<void>;
  updateComment: (irregularId: number, comment: string) => Promise<void>;
  toggleChecked: (id: number) => void;
  clearChecked: () => void;
  exportToExcel: () => Promise<void>;
  rebuildPayments: () => Promise<void>;
  isRebuildingPayments: boolean;
  openGroupAnalysis: (items: IrregularDeltaItem[]) => void;
  toggleGroupAnalysisCheck: (id: number) => void;
  setGroupAnalysisAllChecked: (checked: boolean) => void;
  applyGroupAnalysis: (overrideAction?: HandleType) => Promise<void>;
  closeGroupAnalysis: () => void;
}

// Tracks the latest meter-values request so stale responses from earlier
// requests are silently discarded when the user selects a different item.
let meterValuesRequestGen = 0;

export const useDataErrorStore = create<DataErrorStore>((set, get) => ({
  items: [],
  totalCount: 0,
  currentPage: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  isLoading: false,
  error: null,
  filters: getDefaultFilters(),

  statusOptions: [],
  locationUsageOptions: [],
  isLoadingFilters: false,

  selectedItem: null,
  meterValues: [],
  isLoadingMeterValues: false,
  meterValuesError: null,
  surroundSize: DEFAULT_SURROUND_SIZE,

  handlingId: null,
  checkedIds: new Set(),
  isExporting: false,

  isRebuildingPayments: false,

  analyzeGroupEntries: [],
  analyzeGroupChecked: new Set(),
  isApplyingGroupFix: false,
  groupAnalysisError: null,

  loadItems: async () => {
    const { currentPage, pageSize, filters } = get();
    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.getIrregularDeltaUseCase.execute(currentPage, pageSize, filters);
      if (result.hasError) {
        set({ error: result.errorMessage, isLoading: false });
      } else {
        set({ items: result.items, totalCount: result.totalCount, isLoading: false });
      }
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load records',
        isLoading: false,
      });
    }
  },

  loadFilters: async () => {
    const { filters } = get();
    set({ isLoadingFilters: true });
    try {
      const services = servicesContainer.getServices();
      const result = await services.getIrregularDeltaFiltersUseCase.execute(
        filters.periodFrom,
        filters.periodTo,
        filters.search
      );

      if (result.hasError) {
        set({ isLoadingFilters: false });
        return;
      }

      // Apply HIVE-5554 defaults on first load: pre-select Pending + SharedBuilding
      const currentStatuses = get().filters.selectedStatuses;
      const currentUsages = get().filters.selectedLocationUsages;

      let newStatuses = currentStatuses;
      let newUsages = currentUsages;

      if (currentStatuses.length === 0) {
        const hasPending = result.statusOptions.some(o => o.key === DEFAULT_STATUS_KEY);
        if (hasPending) newStatuses = [DEFAULT_STATUS_KEY];
      }

      if (currentUsages.length === 0) {
        const hasSharedBuilding = result.locationUsageOptions.some(
          o => o.key === DEFAULT_LOCATION_USAGE_KEY
        );
        if (hasSharedBuilding) newUsages = [DEFAULT_LOCATION_USAGE_KEY];
      }

      set({
        statusOptions: result.statusOptions,
        locationUsageOptions: result.locationUsageOptions,
        isLoadingFilters: false,
        filters: {
          ...get().filters,
          selectedStatuses: newStatuses,
          selectedLocationUsages: newUsages,
        },
      });
    } catch {
      set({ isLoadingFilters: false });
    }
  },

  loadPage: async (page: number) => {
    set({ currentPage: page });
    await get().loadItems();
  },

  setPageSize: (size: number) => {
    set({ pageSize: size, currentPage: 0 });
    get().loadItems();
  },

  setFilters: (f: Partial<DataErrorFilters>) => {
    set({ filters: { ...get().filters, ...f }, currentPage: 0 });
    get().loadFilters();
    get().loadItems();
  },

  selectItem: (item: IrregularDeltaItem | null) => {
    set({ selectedItem: item, meterValues: [], meterValuesError: null });

    if (!item) return;

    const gen = ++meterValuesRequestGen;
    const { surroundSize } = get();
    set({ isLoadingMeterValues: true });

    const services = servicesContainer.getServices();
    services.getMeterValuesSurroundUseCase
      .execute(item.toMeterValueId, item.machineId, surroundSize)
      .then(result => {
        if (gen !== meterValuesRequestGen) return; // discard stale response
        if (result.hasError) {
          set({ meterValuesError: result.errorMessage, isLoadingMeterValues: false });
        } else {
          set({ meterValues: result.values, isLoadingMeterValues: false });
        }
      })
      .catch((error: unknown) => {
        if (gen !== meterValuesRequestGen) return;
        set({
          meterValuesError: error instanceof Error ? error.message : 'Failed to load meter values',
          isLoadingMeterValues: false,
        });
      });
  },

  // Opens the MeterValuesBlade with pre-loaded data from the group analysis —
  // no API call needed since the data was already fetched by openGroupAnalysis.
  previewItem: (item: IrregularDeltaItem, meterValues: DeltaMeterValue[]) => {
    ++meterValuesRequestGen; // invalidate any in-flight surround requests
    set({ selectedItem: item, meterValues, meterValuesError: null, isLoadingMeterValues: false });
  },

  setSurroundSize: (n: number) => {
    if (![1, 3, 5, 10].includes(n)) return;
    set({ surroundSize: n });
    const { selectedItem } = get();
    if (!selectedItem) return;

    const gen = ++meterValuesRequestGen;
    set({ isLoadingMeterValues: true, meterValuesError: null });
    const services = servicesContainer.getServices();
    services.getMeterValuesSurroundUseCase
      .execute(selectedItem.toMeterValueId, selectedItem.machineId, n)
      .then(result => {
        if (gen !== meterValuesRequestGen) return;
        if (result.hasError) {
          set({ meterValuesError: result.errorMessage, isLoadingMeterValues: false });
        } else {
          set({ meterValues: result.values, isLoadingMeterValues: false });
        }
      })
      .catch((error: unknown) => {
        if (gen !== meterValuesRequestGen) return;
        set({
          meterValuesError: error instanceof Error ? error.message : 'Failed to load meter values',
          isLoadingMeterValues: false,
        });
      });
  },

  handleItem: async (irregularId: number, type: HandleType, comment: string) => {
    set({ handlingId: irregularId });
    try {
      const services = servicesContainer.getServices();
      const result = await services.handleIrregularDeltaUseCase.execute(
        irregularId,
        type,
        comment || null
      );

      if (!result.isSuccess) {
        set({ handlingId: null, error: result.errorMessage });
        return;
      }

      // Reload to get fresh statusId from server
      set({ handlingId: null });
      await get().loadItems();
    } catch (error: unknown) {
      set({
        handlingId: null,
        error: error instanceof Error ? error.message : 'Action failed',
      });
    }
  },

  batchHandle: async (type: HandleType, comment: string) => {
    const { checkedIds } = get();
    if (checkedIds.size === 0) return;

    const items: HandleActionRequest[] = Array.from(checkedIds).map(id => ({
      irregularId: id,
      handleType: type,
      comment: comment || null,
    }));

    set({ isLoading: true });
    try {
      const services = servicesContainer.getServices();
      await services.batchHandleIrregularDeltaUseCase.execute(items);
      set({ checkedIds: new Set() });
      await get().loadItems();
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Batch action failed',
        isLoading: false,
      });
    }
  },

  updateComment: async (irregularId: number, comment: string) => {
    const originalComment = get().items.find(i => i.irregularId === irregularId)?.comments ?? '';

    set(state => ({
      items: state.items.map(item =>
        item.irregularId === irregularId ? { ...item, comments: comment } : item
      ),
    }));

    try {
      const services = servicesContainer.getServices();
      await services.updateDeltaCommentUseCase.execute(irregularId, comment || null);
    } catch {
      set(state => ({
        items: state.items.map(item =>
          item.irregularId === irregularId ? { ...item, comments: originalComment } : item
        ),
        error: 'Failed to save comment',
      }));
    }
  },

  toggleChecked: (id: number) => {
    set(state => {
      const next = new Set(state.checkedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { checkedIds: next };
    });
  },

  clearChecked: () => set({ checkedIds: new Set() }),

  exportToExcel: async () => {
    const { filters } = get();
    set({ isExporting: true });
    try {
      const services = servicesContainer.getServices();
      const result = await services.exportIrregularDeltaUseCase.execute(filters);
      if (result.hasError) {
        set({ error: result.errorMessage, isExporting: false });
        return;
      }

      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'irregular-delta-report.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      set({ isExporting: false });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Export failed',
        isExporting: false,
      });
    }
  },

  rebuildPayments: async () => {
    set({ isRebuildingPayments: true, error: null });
    try {
      const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;
      if (!operatorId) {
        set({ error: 'No operator selected', isRebuildingPayments: false });
        return;
      }
      const services = servicesContainer.getServices();
      const result = await services.rebuildPaymentsUseCase.execute(operatorId);
      if (!result.isSuccess) {
        set({ error: result.errorMessage ?? 'Rebuild failed', isRebuildingPayments: false });
        return;
      }
      set({ isRebuildingPayments: false });
    } catch (err: unknown) {
      set({
        error: err instanceof Error ? err.message : 'Rebuild failed',
        isRebuildingPayments: false,
      });
    }
  },

  openGroupAnalysis: (items: IrregularDeltaItem[]) => {
    const pending = items.filter(i => !i.handled);
    if (pending.length === 0) return;

    const entries: AnalyzeGroupEntry[] = pending.map(item => ({
      item,
      meterValues: [],
      suggestion: null,
      isLoading: true,
      error: null,
    }));

    set({ analyzeGroupEntries: entries, analyzeGroupChecked: new Set(), isApplyingGroupFix: false, groupAnalysisError: null });

    const services = servicesContainer.getServices();
    const surroundSize = get().surroundSize;

    pending.forEach(item => {
      services.getMeterValuesSurroundUseCase
        .execute(item.toMeterValueId, item.machineId, surroundSize)
        .then(result => {
          set(state => {
            const updated = state.analyzeGroupEntries.map(e => {
              if (e.item.irregularId !== item.irregularId) return e;
              if (result.hasError) return { ...e, isLoading: false, error: result.errorMessage ?? 'Failed' };
              const suggestion = analyzeDelta(item, result.values);
              return { ...e, meterValues: result.values, suggestion, isLoading: false };
            });

            // Auto-check entries where suggestion is high confidence
            const autoChecked = new Set(state.analyzeGroupChecked);
            const entry = updated.find(e => e.item.irregularId === item.irregularId);
            if (entry?.suggestion?.confidence === 'high') {
              autoChecked.add(item.irregularId);
            }

            return { analyzeGroupEntries: updated, analyzeGroupChecked: autoChecked };
          });
        })
        .catch(() => {
          set(state => ({
            analyzeGroupEntries: state.analyzeGroupEntries.map(e =>
              e.item.irregularId === item.irregularId
                ? { ...e, isLoading: false, error: 'Failed to load' }
                : e
            ),
          }));
        });
    });
  },

  toggleGroupAnalysisCheck: (id: number) => {
    set(state => {
      const next = new Set(state.analyzeGroupChecked);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { analyzeGroupChecked: next };
    });
  },

  setGroupAnalysisAllChecked: (checked: boolean) => {
    set(state => ({
      analyzeGroupChecked: checked
        ? new Set(state.analyzeGroupEntries.filter(e => e.suggestion !== null).map(e => e.item.irregularId))
        : new Set(),
    }));
  },

  applyGroupAnalysis: async (overrideAction?: HandleType) => {
    const { analyzeGroupEntries, analyzeGroupChecked } = get();
    const requests: HandleActionRequest[] = analyzeGroupEntries
      .filter(e => analyzeGroupChecked.has(e.item.irregularId) && (overrideAction != null || e.suggestion !== null))
      .map(e => ({
        irregularId: e.item.irregularId,
        handleType: overrideAction ?? e.suggestion!.action,
        comment: null,
      }));

    if (requests.length === 0) return;

    set({ isApplyingGroupFix: true });
    try {
      const services = servicesContainer.getServices();
      await services.batchHandleIrregularDeltaUseCase.execute(requests);
      set({ analyzeGroupEntries: [], analyzeGroupChecked: new Set(), isApplyingGroupFix: false });
      await get().loadItems();
    } catch (error: unknown) {
      set({
        groupAnalysisError: error instanceof Error ? error.message : 'Failed to apply fixes',
        isApplyingGroupFix: false,
      });
    }
  },

  closeGroupAnalysis: () => {
    set({ analyzeGroupEntries: [], analyzeGroupChecked: new Set(), isApplyingGroupFix: false, groupAnalysisError: null });
  },
}));
